import type { Href } from "expo-router";
import { router } from "expo-router";
import { useEffect } from "react";
import { Platform, Pressable, Text, View } from "react-native";

import { track } from "../lib/analytics";
import { COLORS, FONTS } from "../lib/brand";
import { PANE_THEMES } from "../components/brand/paneThemes";

const THEME = PANE_THEMES.General;
const BLACK = COLORS.BLACK.hex;
const FOREST = COLORS.FOREST.hex;
const AMBER = THEME.accent;
const LINEN = THEME.textOnSurface;
const TEXT_MUTED = THEME.textOnSurfaceDim;
const BORDER = "rgba(247,244,238,0.12)";

/**
 * Custom 404 page. Expo Router catches every unmatched route through
 * `+not-found.tsx`. We render a calm, dark-themed stub with two
 * routes out (home + sign in) and fire one analytics event so we can
 * see which URLs people are landing on that 404 — most often dead
 * marketing links or stale shared URLs from prior schema versions.
 */
export default function NotFoundScreen() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    track("not_found_page_viewed", { path: window.location.pathname });
  }, []);

  return (
    <View
      style={{
        flex: 1,
        backgroundColor: BLACK,
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <Text
        style={{
          fontFamily: FONTS.display,
          fontSize: 96,
          lineHeight: 96,
          color: LINEN,
          letterSpacing: -2,
        }}
      >
        404
      </Text>
      <Text
        style={{
          marginTop: 8,
          fontFamily: FONTS.display,
          fontSize: 24,
          color: LINEN,
        }}
      >
        We can't find that page.
      </Text>
      <Text
        style={{
          marginTop: 8,
          fontFamily: FONTS.body,
          fontSize: 14,
          lineHeight: 21,
          color: TEXT_MUTED,
          textAlign: "center",
          maxWidth: 380,
        }}
      >
        It might have moved, or never existed. Either way, let's get you somewhere useful.
      </Text>
      <View style={{ marginTop: 28, flexDirection: "row", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
        <Pressable
          onPress={() => router.replace("/" as Href)}
          style={[
            {
              paddingHorizontal: 22,
              paddingVertical: 12,
              borderRadius: 6,
              backgroundColor: AMBER,
            },
            Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
          ]}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 11.5,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: FOREST,
            }}
          >
            Take me home
          </Text>
        </Pressable>
        <Pressable
          onPress={() => router.replace("/login" as Href)}
          style={[
            {
              paddingHorizontal: 18,
              paddingVertical: 12,
              borderRadius: 6,
              borderWidth: 1,
              borderColor: BORDER,
            },
            Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
          ]}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyMedium,
              fontSize: 11.5,
              letterSpacing: 1.2,
              textTransform: "uppercase",
              color: TEXT_MUTED,
            }}
          >
            Sign in
          </Text>
        </Pressable>
      </View>
    </View>
  );
}
